/* eslint-disable import/no-cycle */
import React from "react";

import ButtonComponent from "../button";

import { theme } from "../../styles/theme";
import {
  LabelStyled,
  RowStyled,
  SectionStyled,
  Title,
  ValueStyled,
  Wrapper,
  Flex,
} from "./styled";

import { FiscalNoteModel } from "../../../domain/models/fiscal-note";

import { formCurrency } from "../../pages/developer/documents/list";
import { NFSeTypeEnum, NFSeTypeLabels } from "../../enums/nfs-e-type.enum";
import TooltipComp from "../tooltip-info-field";
import { parseDateWithoutTimezone } from "../../../utils";

type PageProps = {
  fiscalNote: FiscalNoteModel | any | undefined;
  submitting?: boolean;
  onConfirmCallback?: () => void;
  onCancelCallback?: () => void;
  titleConfirmButton?: string;
};

const ResumeFiscalNote: React.FC<PageProps> = ({
  fiscalNote,
  onConfirmCallback,
  onCancelCallback,
  submitting,
  titleConfirmButton = "Confirmar e Gerar a Nota"
}) => {
  const serviceValue =
    fiscalNote?.serviceValue?.replaceAll(".", "")?.replaceAll(",", ".") || "0";

  const formattedServiceValue = formCurrency.format(Number(serviceValue));

  const baseCalculationValue = formattedServiceValue;

  const aliquot = Number(
    fiscalNote?.iss === "true" ? fiscalNote?.issAliquot : 0,
  )
    .toFixed(2)
    .replace(".", ",");

  const issValue = formCurrency.format(
    (Number(parseFloat(aliquot)) / 100) * Number(serviceValue),
  );

  const totalValue = formattedServiceValue;

  const getDateRecurrent = (): string => {
    if (!fiscalNote?.dayOfRecurrence) return "-";
    const date = parseDateWithoutTimezone(fiscalNote?.dayOfRecurrence);
    return date.toLocaleDateString();
  };

  const getDayRecurrent = (): string => {
    if (!fiscalNote?.dayOfRecurrence) return "-";
    const date = parseDateWithoutTimezone(fiscalNote?.dayOfRecurrence);
    return date.getDate().toString().padStart(2, "0");
  };

  return (
    <Wrapper>
      <SectionStyled>
        <Title>Tomador do Serviço:</Title>
        <RowStyled>
          <LabelStyled>Razão Social/Nome:</LabelStyled>
          <ValueStyled>
            {fiscalNote?.taker?.socialReason ??
              fiscalNote?.company?.selected?.label}
          </ValueStyled>
        </RowStyled>
        <Title>Tipo de nota fiscal:</Title>
        <RowStyled>
          <LabelStyled>Tipo:</LabelStyled>
          <ValueStyled>
            {fiscalNote?.hasNationalFiscal === true ?
              NFSeTypeLabels[NFSeTypeEnum.NFS_E_NACIONAL] :
              NFSeTypeLabels[NFSeTypeEnum.NFS_E]
            }
          </ValueStyled>
        </RowStyled>
        {fiscalNote?.taker?.isForeignTaker && (
          <>
            <RowStyled>
              <LabelStyled>Tomador estrangeiro:</LabelStyled>
              <ValueStyled>Sim</ValueStyled>
            </RowStyled>
            {fiscalNote?.taker?.codeForeign && (
              <RowStyled>
                <LabelStyled>NIF:</LabelStyled>
                <ValueStyled>{fiscalNote.taker.codeForeign}</ValueStyled>
              </RowStyled>
            )}
            {fiscalNote?.taker?.naoNif && (
              <RowStyled>
                <LabelStyled>Motivo ausência NIF:</LabelStyled>
                <ValueStyled>{fiscalNote.taker.naoNif}</ValueStyled>
              </RowStyled>
            )}
            <RowStyled>
              <LabelStyled>País:</LabelStyled>
              <ValueStyled>
                {fiscalNote?.taker?.address?.descriptionCountry || "-"}
              </ValueStyled>
            </RowStyled>
          </>
        )}
        {!fiscalNote?.taker?.isForeignTaker && (
          <RowStyled>
            <LabelStyled>CPF/CNPJ:</LabelStyled>
            <ValueStyled>
              {fiscalNote?.taker?.documentNumber ??
                fiscalNote?.subscriptionNumber}
            </ValueStyled>
          </RowStyled>
        )}
        <RowStyled>
          <LabelStyled>Endereço:</LabelStyled>
          <ValueStyled>
            {`${fiscalNote?.taker?.address?.place ?? fiscalNote?.address?.place
              } 
            ${fiscalNote?.taker?.address?.number ?? fiscalNote?.address?.number
              }`}
          </ValueStyled>
        </RowStyled>

        <RowStyled>
          <RowStyled style={{ width: "75%" }}>
            <LabelStyled>Município:</LabelStyled>
            <ValueStyled>
              {`${fiscalNote?.taker?.address?.city ??
                fiscalNote?.city?.selected?.label
                }`}
            </ValueStyled>
          </RowStyled>
          <RowStyled className="ms-5" style={{ width: "25%" }}>
            <LabelStyled>UF:</LabelStyled>
            <ValueStyled>
              {`${fiscalNote?.taker?.address?.state ??
                fiscalNote?.state?.selected?.label
                }`}
            </ValueStyled>
          </RowStyled>
        </RowStyled>
      </SectionStyled>

      <SectionStyled>
        <Title>Atividade (CNAE):</Title>
        <RowStyled>
          <ValueStyled className="w-100">{fiscalNote?.cnaeLabel || "-"}</ValueStyled>
        </RowStyled>
      </SectionStyled>
      <SectionStyled>
        <Title>Serviço Nacional:</Title>
        <RowStyled>
          <ValueStyled className="w-100">{fiscalNote?.nationalServiceLabel || "-"}</ValueStyled>
        </RowStyled>
      </SectionStyled>
      <SectionStyled>
        <Title>NBS:</Title>
        <RowStyled>
          <ValueStyled className="w-100">{fiscalNote?.nbsLabel || "-"}</ValueStyled>
        </RowStyled>
      </SectionStyled>
      {fiscalNote?.hasForeignService && (
        <SectionStyled>
          <Title>Serviço para Exterior:</Title>
          <RowStyled>
            <LabelStyled>Tipo de moeda:</LabelStyled>
            <ValueStyled>{fiscalNote?.currencyType || "-"}</ValueStyled>
          </RowStyled>
          <RowStyled>
            <LabelStyled>Valor (moeda estrangeira):</LabelStyled>
            <ValueStyled>{fiscalNote?.foreignServiceValue ?? "-"}</ValueStyled>
          </RowStyled>
        </SectionStyled>
      )}
      <SectionStyled>
        <Title>Descrição dos Serviços:</Title>
        <RowStyled>
          <ValueStyled className="p-3">{`${fiscalNote?.description || ""}`}</ValueStyled>
        </RowStyled>
      </SectionStyled>
      <br />
      <SectionStyled>
        <Title>Gerar de forma recorrente: <span className="text-white">{`${fiscalNote?.hasRecurrence === "true" ? "Sim" : "Não"}`}</span></Title>
      </SectionStyled>
      {fiscalNote?.hasRecurrence === "true" && (
        <SectionStyled>
          <Flex>
            <Title>
              Dia da recorrência:
            </Title>
            <span className="text-white">
              {getDateRecurrent()}
            </span>
            <TooltipComp
              message={`A recorrência será gerada todo mês no dia ${getDayRecurrent()} a partir da data da primeira nota.`}
            />
          </Flex>
        </SectionStyled>
      )}
      <SectionStyled>
        <Title>Valores:</Title>
        <RowStyled>
          <ValueStyled className="d-flex justify-content-space-between w-100 p-3">
            <div className="flex-column d-flex align-items-center w-25">
              <span>(A)</span>
              <span>Valor do Serviço</span>
              {formattedServiceValue}
            </div>
            <div className="flex-column d-flex align-items-center w-25">
              <span>C=(A)</span>
              <span>Base de Cálculo</span>
              {baseCalculationValue}
            </div>
            <div className="flex-column d-flex align-items-center w-25">
              <span>(E)</span>
              <span>Alíq. (%)</span>
              {aliquot}
            </div>
            <div className="flex-column d-flex align-items-center w-25">
              <span>F=(C*E%)</span>
              <span>Valor ISS</span>
              {issValue}
            </div>
          </ValueStyled>
        </RowStyled>
        <RowStyled>
          <ValueStyled className="w-100 p-2">
            Impostos Adicionais(Os valores informados são de responsabilidade do
            emissor):
          </ValueStyled>
        </RowStyled>
        <RowStyled>
          <ValueStyled
            className="d-flex justify-content-space-between w-75 p-3"
            style={{ justifyContent: "space-between" }}
          >
            <div className="flex-column d-flex align-items-start pe-3">
              <span>IR (RS):</span>
              <span>INSS (RS):</span>
            </div>
            <div className="flex-column d-flex align-items-start">
              <span>Confins (RS):</span>
              <span>Pis/Pasep (RS):</span>
            </div>
            <div className="flex-column d-flex align-items-start">
              <span>CSLL (RS):</span>
              <span>Outros (RS):</span>
            </div>
          </ValueStyled>
          <div className="flex-column d-flex align-items-center w-25">
            <span className="d-flex justify-content-center">Valor Total</span>
            <span className="d-flex justify-content-center">da Nota:</span>
            <span className="d-flex justify-content-center">{totalValue}</span>
          </div>
        </RowStyled>
      </SectionStyled>
      <div className="d-flex justify-content-center mt-4 mobile-style">
        <div className="me-3">
          <ButtonComponent
            title={titleConfirmButton}
            backgroundColor={theme.colors.blue400}
            color={theme.colors.white}
            onClick={onConfirmCallback}
            disabled={submitting}
          />
        </div>
        <div className="ms-3">
          <ButtonComponent
            title="Cancelar"
            backgroundColor={theme.colors.lightBlue}
            color={theme.colors.darkBlue}
            onClick={onCancelCallback}
          />
        </div>
      </div>
    </Wrapper>
  );
};
export default ResumeFiscalNote;
